import { PlacesType } from 'react-tooltip';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { Tooltip } from 'components/Widget/Tooltip';
import Icon, { IconType } from 'components/Icon';
import * as Base from '../styles';
import * as S from './styles';

interface OptionRowProps {
	label: string | IconType | null;
	value: string;
	values: string[];
	onChange?: (value: string) => void;
	readonly?: boolean;
	disabled?: boolean;

	// optional tooltip will appear on hover
	tooltip?: string;

	// optional placement of tooltip, can be "top", "right", "bottom", or "left". defaults to "top"
	tooltipPlacement?: PlacesType;
}

const OptionRow = ({
	label,
	value,
	values,
	onChange,
	readonly,
	disabled,
	tooltip,
	tooltipPlacement,
}: OptionRowProps) => {
	const theme = useTheme();
	const [tooltipId] = useState(String(Math.random()));
	const [isMouseDown, setIsMouseDown] = useState(false);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const handleMouseDown = (option: string) => {
		if (!disabled && !readonly) {
			setIsMouseDown(true);
			onChange?.(option);
		}
	};

	const handleMouseEnter = (option: string) => {
		if (isMouseDown && !disabled && !readonly) {
			onChange?.(option);
		}
	};

	const handleMouseUp = () => {
		setIsMouseDown(false);
	};

	const handleMouseLeave = () => {};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderLabel = () => {
		if (!label) return null;
		if (typeof label === 'string') {
			return <Base.LetterLabel>{label}</Base.LetterLabel>;
		}
		return (
			<S.IconWrapper>
				<Icon width={14} height={14} color={theme.gray['700']} type={label} />
			</S.IconWrapper>
		);
	};

	const renderLabelWithTooltip = () => {
		if (tooltip) {
			return (
				<>
					<a
						data-tooltip-id={tooltipId}
						data-tooltip-content={tooltip}
						style={{ display: 'flex', alignItems: 'center' }}
					>
						{renderLabel()}
					</a>
					<Tooltip id={tooltipId} placement={tooltipPlacement || 'top'} />
				</>
			);
		}
		return renderLabel();
	};

	return (
		<S.Base disabled={disabled}>
			{renderLabelWithTooltip()}

			<S.Options>
				{values.map((option, index) => (
					<S.OptionButton
						key={index}
						selected={value === option}
						disabled={disabled}
						onMouseDown={() => handleMouseDown(option)}
						onMouseEnter={() => handleMouseEnter(option)}
						onMouseUp={() => handleMouseUp()}
						onMouseLeave={handleMouseLeave}
					>
						<S.OptionLabel selected={value === option} disabled={disabled}>
							{option}
						</S.OptionLabel>
					</S.OptionButton>
				))}
			</S.Options>
		</S.Base>
	);
};

export default OptionRow;
